import {Box, Typography} from '@mui/material';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import {ReactComponent as InfoIcon} from '../../../assets/images/info-icon.svg';
import {t} from 'i18next';
import ArrTextField from '../../common/arr-text-field/ArrTextField';
import {useCallback, useEffect, useRef, useState} from 'react';
import ArrButton from '../../common/arr-button/ArrButton';
import {ReactComponent as AddIcon} from '../../../assets/images/add.svg';
import './ManageClients.scss';
import {useClients} from '../../../hooks/clients/useClients';
import {GridColDef} from '@mui/x-data-grid/models/colDef';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {GridRenderCellParams, GridSortModel} from '@mui/x-data-grid';
import {AddClient} from '../add-clients/AddClient';
import ArrIconButton from '../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as MoreIcon} from '../../../assets/images/icon-more.svg';
import ArrMenu, {
  ArrMenuHandle,
  CustomMenuItem,
} from '../../common/arr-menu/ArrMenu';
import dayjs from 'dayjs';
import {ToastType} from '../../../shared/constants';
import DeleteClientModal from './DeleteClientModal';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {IClient} from '../../../api/types/client.type';

export const clientsActionsMoreMenu: CustomMenuItem[] = [
  {label: t('common.edit'), id: 'edit'},
  {label: t('common.delete'), id: 'delete'},
];

const ROW_HEIGHT = 36;

const ManageClients = () => {
  const [searchByNameText, setSearchByNameText] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'entityLabel',
      sort: 'asc',
    },
  ]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const actionMenuRef = useRef<ArrMenuHandle | null>(null);
  const [appliedSearchText, setAppliedSearchText] = useState('');
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastStatus, setToastStatus] = useState<ToastType[]>([]);
  const [selectedClient, setSelectedClient] = useState<IClient | null>(null);
  const [isAddClient, setIsAddClient] = useState<boolean>(false);
  const [isEdit, setIsEdit] = useState(false);

  const handleMoreIconClick = (
    e: React.MouseEvent<HTMLElement>,
    clientData: IClient
  ) => {
    actionMenuRef.current?.open(e.currentTarget as HTMLElement);
    setSelectedClient(clientData);
  };

  const handleFiltersApply = () => {
    setAppliedSearchText(searchByNameText);
  };
  const triggerToast = (message: string, status: ToastType[]) => {
    setToastMessage(message);
    setToastStatus(status);
    setShowToast(true);
  };

  const manageClientsColumns: GridColDef[] = [
    {
      field: 'entityLabel',
      headerName: t('manageClients.columns.clientName'),
      flex: 1,
      valueFormatter: (value) => {
        return new DOMParser().parseFromString(value, 'text/html')
          .documentElement.textContent;
      },
    },
    {
      field: 'entityCode',
      headerName: t('manageClients.columns.clientShortName'),
      flex: 1,
    },
    {
      field: 'entityDomainID',
      headerName: t('manageClients.columns.domainId'),
      flex: 1,
    },
    {
      field: 'createdByName',
      headerName: t('manageClients.columns.createdBy'),
      flex: 1,
    },
    {
      field: 'createdOn',
      headerName: t('manageClients.columns.createdOn'),
      flex: 1,
      valueFormatter: (value) => {
        return `${dayjs(value).format('YYYY/MM/DD HH:mm:ss')}`;
      },
    },
    {
      field: '',
      headerName: '',
      width: 50,
      sortable: false,
      filterable: false,
      headerClassName: 'typo-text label2_size more-option-header',
      renderCell: (params: GridRenderCellParams) => {
        return (
          <ArrIconButton
            onClick={(e) => handleMoreIconClick(e, params.row)}
            icon={<MoreIcon />}
            ariaLabel={t('manageClients.moreActions')}
            aria-haspopup="true"
            aria-expanded={Boolean(selectedClient)}
            shape="none"
            testId="client-more-action-btn"
            className="client-grid-icon-button"
          />
        );
      },
    },
  ];

  const onMenuItemClick = useCallback(
    (item: {id: string}) => {
      if (!selectedClient) return;
      if (item.id === 'delete') {
        setOpenDeleteModal(true);
      } else if (item.id === 'edit') {
        setIsAddClient(true);
        setIsEdit(true);
      }
      actionMenuRef.current?.close();
    },
    [selectedClient]
  );

  const onPageChangeHandler = (newPage: number) => {
    setPageNumber(newPage);
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    setSortModel(sortModel);
  };

  const {data: clients, isLoading: isClientsLoading} = useClients({
    EntityLabel: appliedSearchText.trim(),
    PageNumber: pageNumber,
    PageSize: itemsPerPage,
    SortingColumn: sortModel[0]?.field || 'entityLabel',
    SortingOrder: sortModel[0]?.sort || 'asc',
  });

  useEffect(() => {
    if (!isAddClient) setIsEdit(false);
  }, [isAddClient]);

  return (
    <Box className="manage-clients-container">
      <Box className="manage-clients-header">
        <Typography
          tabIndex={0}
          className="typo-text headline3_size manage-clients-title-text"
          data-testid={`manage-clients-title`}
        >
          {t('manageClients.title')}
        </Typography>
        &nbsp;
        <ArrTooltipWrapper
          title={t('manageClients.title')}
          trigger={
            <InfoIcon
              fontSize="medium"
              className="manage-clients-info-icon"
              data-testid="manage-clients-info-icon"
              tabIndex={0}
            />
          }
        >
          <p data-testid={`manage-clients-p1`}>
            {' '}
            {t('manageClients.tooltip.p1')}
          </p>
          <p
            data-testid={`manage-clients-using-filter-p2-p3a-add-new-client-p3b-using-edit-p4-using-delete-p5`}
          >
            {t('manageClients.tooltip.using')}
            <strong data-testid={`manage-clients-filter`}>
              {t('manageClients.tooltip.filter')}
            </strong>
            {t('manageClients.tooltip.p2')}
            <br data-testid={`manage-clients-br`} />
            {t('manageClients.tooltip.p3.p3a')}
            <strong data-testid={`manage-clients-add-new-client`}>
              {t('manageClients.tooltip.addNewClient')}
            </strong>
            {t('manageClients.tooltip.p3.p3b')}
            <br data-testid={`manage-clients-br-btn`} />
            {t('manageClients.tooltip.using')}
            <strong data-testid={`manage-clients-edit`}>
              {t('manageClients.tooltip.edit')}
            </strong>
            {t('manageClients.tooltip.p4')}
            <br data-testid={`manage-clients-br-button`} />
            {t('manageClients.tooltip.using')}
            <strong data-testid={`manage-clients-delete`}>
              {t('manageClients.tooltip.delete')}
            </strong>
            {t('manageClients.tooltip.p5')}
          </p>
        </ArrTooltipWrapper>
      </Box>
      <Box className="manage-clients-filter-panel">
        <Box className="manage-clients-filter-panel-controls">
          <div
            className="manage-clients-item-flex"
            data-testid={`manage-clients-div`}
          >
            <ArrTextField
              state="enabled"
              sizeVariant="medium"
              slotProps={{inputLabel: {shrink: false}}}
              placeholder={t('manageClients.filters.filterByName')}
              aria-label={t('manageClients.filters.filterByName')}
              value={searchByNameText}
              onChange={(e) => setSearchByNameText(e.target.value)}
              dataTestId="manage-clients-filter-by-name"
              type="clear"
            />
          </div>
          <ArrButton
            aria-label={t('manageClients.filters.applyFilter')}
            variant="secondary"
            size="medium"
            onClick={handleFiltersApply}
            dataTestId="apply-filter-btn"
          >
            {t('manageClients.filters.applyFilter')}
          </ArrButton>
        </Box>
        <Box>
          <ArrButton
            variant="primary"
            size="small"
            dataTestId="new-client-button"
            startIcon={<AddIcon className="add-icon" />}
            onClick={() => {
              setIsAddClient(true);
            }}
          >
            {t('manageClients.addNewClient')}
          </ArrButton>
          {isAddClient && (
            <AddClient
              showDrawer={isAddClient}
              handleDrawer={setIsAddClient}
              selectedClient={selectedClient}
              isEdit={isEdit}
              triggerToast={triggerToast}
            />
          )}
        </Box>
      </Box>
      <Box className="manage-clients-grid" data-testid="manage-clients-table">
        <ArrDataGrid
          aria-label={t('manageClients.title')}
          rowHeight={ROW_HEIGHT}
          rows={clients?.data || []}
          columns={manageClientsColumns}
          rowCount={clients?.totalRecords || 0}
          currentPage={pageNumber}
          itemsPerPage={itemsPerPage}
          pageSize={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          paginationMode="server"
          onPageChange={onPageChangeHandler}
          entityType={t('manageClients.entity')}
          getRowId={(row) => row.entityId}
          onSortModelChange={handleSortModelChange}
          sortModel={sortModel}
          className="manage-clients-grid"
          loading={isClientsLoading}
          showPerPageOptions={true}
          pageSizeOptions={[10, 25, 50, 100]}
          sortingMode="server"
          disableVirtualization={true}
        />
        <ArrMenu
          id="more-menu"
          ref={actionMenuRef}
          items={clientsActionsMoreMenu}
          onItemClick={onMenuItemClick}
          direction="left"
        />
      </Box>
      <DeleteClientModal
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        selectedClient={selectedClient}
        triggerToast={triggerToast}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          status={toastStatus}
          onClose={() => setShowToast(false)}
        />
      )}
    </Box>
  );
};

export default ManageClients;
